/**
 * Match Odds Component
 * Displays match odds with back and lay betting options
 * Uses React Query for optimized data fetching
 */

import React from 'react';
import { useBetting } from '@/Context/BettingContext';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useMatchOdds } from '@/hooks/useMatchOdds';
import styles from './MatchOdds.module.css';

const MatchOdds = ({ matchData }) => {
  const { addBet } = useBetting();
  const { isMobile } = useScreenSize();
  const matchId = matchData?.id || matchData?.matchId;
  
  // Use React Query hook for odds data
  const { data: oddsData, isLoading, error } = useMatchOdds(matchId, matchData, isMobile);

  // Handle odds click
  const handleOddsClick = (team, outcome, type, odds, volume) => {
    if (!matchData || odds === '--' || matchData.isSuspended) return;

    const matchId = matchData?.id || matchData?.matchId;
    const betDetails = {
      matchId: matchId,
      matchTitle: `${matchData.team1} v ${matchData.team2}`,
      team1: matchData.team1,
      team2: matchData.team2,
      selectionName: team,
      category: outcome === 'one' ? 'matched' : (outcome === 'x' ? 'x' : 'two'),
      type,
      odds,
      volume: volume || 0,
      isBlue: type === 'back',
      stake: '',
      liability: '0.00'
    };

    addBet(betDetails);
  };

  if (!matchData) {
    return (
      <div className={styles.matchOdds}>
        <p className={styles.noData}>No match data available</p>
      </div>
    );
  }

  // Only show loader on initial load (when no data exists yet)
  // Show data instantly when available
  if (isLoading && !oddsData) {
    return (
      <div className={styles.matchOdds}>
        <div className={styles.loadingContainer}>
          <div className={styles.loader}>
            <span className={styles.loaderText}>loading</span>
            <span className={styles.load}></span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !oddsData || oddsData.length === 0) {
    return (
      <div className={styles.matchOdds}>
        <p className={styles.errorText}>
          {error?.message || 'No odds available'}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.matchOdds}>
      <div className={styles.oddsTable}>
        <div className={styles.tableHeader}>
          <div className={styles.headerCell}>Selection</div>
          <div className={styles.headerCell}>Back</div>
          <div className={styles.headerCell}>Lay</div>
        </div>

        <div className={styles.tableBody}>
          {oddsData.map((row, index) => (
            <div key={`${row.team}-${index}`} className={styles.tableRow}>
              <div className={styles.selectionCell}>{row.team}</div>
              
              {/* Back Odds - 3 options */}
              <div className={styles.oddsGroup}>
                {row.backOdds.map((odd, i) => (
                  <button
                    key={`back-${i}`}
                    className={`${styles.oddsButton} ${styles.oddsButtonBack} ${styles[`backPosition${i}`]} ${odd.value === '--' ? styles.disabled : ''}`}
                    onClick={() => handleOddsClick(row.team, row.outcome, 'back', odd.value, odd.volume)}
                    disabled={odd.value === '--' || matchData.isSuspended}
                  >
                    <span className={styles.oddsValue}>{odd.value}</span>
                    {odd.volume > 0 && (
                      <span className={styles.oddsVolume}>{odd.volume.toLocaleString()}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Lay Odds - 3 options */}
              <div className={styles.oddsGroup}>
                {row.layOdds.map((odd, i) => (
                  <button
                    key={`lay-${i}`}
                    className={`${styles.oddsButton} ${styles.oddsButtonLay} ${styles[`layPosition${i}`]} ${odd.value === '--' ? styles.disabled : ''}`}
                    onClick={() => handleOddsClick(row.team, row.outcome, 'lay', odd.value, odd.volume)}
                    disabled={odd.value === '--' || matchData.isSuspended}
                  >
                    <span className={styles.oddsValue}>{odd.value}</span>
                    {odd.volume > 0 && (
                      <span className={styles.oddsVolume}>{odd.volume.toLocaleString()}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MatchOdds;
